import React, { useState, useRef, useEffect } from 'react';
import ChallengeSearchItem from './ChallengeSearchItem';
import AthleteSearchItem from './AthleteSearchItem';
import SegmentSearchItem from './SegmentSearchItem';

const GlobalSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({
    challenges: [],
    athletes: [],
    segments: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        closeSearch();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const closeSearch = () => {
    setShowResults(false);
    setQuery('');
    setResults({ challenges: [], athletes: [], segments: [] });
  };

  const handleInputChange = (e) => {
    const searchQuery = e.target.value;
    setQuery(searchQuery);

    if (searchQuery.length > 0) {
      performSearch(searchQuery);
    } else {
      closeSearch();
    }
  };

  const performSearch = async (queryStr) => {
    setIsLoading(true);
    setShowResults(true);

    try {
      const response = await fetch('/api/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ queryStr: queryStr }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      setResults(data);
    } catch (error) {
      console.error('Search error:', error);
      setResults({ challenges: [], athletes: [], segments: [] });
    } finally {
      setIsLoading(false);
    }
  };

  const renderChallenges = () => {
    if (!results.challenges || results.challenges.length === 0) {
      return null;
    }

    return (
      <>
        <div className='px-4 md:px-6 pt-4'>
          <h5 className='m-0 text-text-grey-600 mini-text font-bold'>
            CHALLENGES
          </h5>
        </div>
        {results.challenges.map((challenge) => (
          <ChallengeSearchItem key={challenge.id} challenge={challenge} />
        ))}
      </>
    );
  };

  const renderAthletes = () => {
    if (!results.athletes || results.athletes.length === 0) {
      return null;
    }

    return (
      <>
        <div className='px-4 md:px-6 pt-4'>
          <h5 className='m-0 text-text-grey-600 mini-text font-bold'>
            ATHLETES
          </h5>
        </div>
        {results.athletes.map((athlete) => (
          <AthleteSearchItem key={athlete.id} athlete={athlete} />
        ))}
      </>
    );
  };

  const renderSegments = () => {
    if (!results.segments || results.segments.length === 0) {
      return null;
    }

    return (
      <>
        <div className='px-4 md:px-6 pt-4'>
          <h5 className='m-0 text-text-grey-600 mini-text font-bold'>
            SEGMENTS
          </h5>
        </div>
        {results.segments.map((segment) => (
          <SegmentSearchItem key={segment.id} segment={segment} />
        ))}
      </>
    );
  };

  const renderResults = () => {
    if (isLoading) {
      return <p className='px-4 md:px-6 py-4'>Loading..</p>;
    }

    const hasChallenges = results.challenges && results.challenges.length > 0;
    const hasAthletes = results.athletes && results.athletes.length > 0;
    const hasSegments = results.segments && results.segments.length > 0;

    if (!hasChallenges && !hasAthletes && !hasSegments && query.length > 0) {
      return <p className='px-4 md:px-6 py-4'>No results</p>;
    }

    return (
      <>
        {renderChallenges()}
        {renderAthletes()}
        {renderSegments()}
      </>
    );
  };

  return (
    <div
      ref={containerRef}
      className='w-full mx-auto search-module-inner-container relative mr-2'
    >
      <form
        onSubmit={(e) => e.preventDefault()}
        id='global-search'
        className='relative'
      >
        <input
          type='text'
          id='global-search-input'
          value={query}
          onChange={handleInputChange}
          className='search-icon focus:bg-background-grey transition-colors search appearance-none border border-transparent bg-background-darker-yellow w-full px-10 py-2 h-[2.25rem] lg:h-fields small-body-text focus:text placeholder:text-text-grey-600 focus:placeholder:text-text-body-inverted text-text-body-inverted rounded'
          placeholder='Search challenges, segments and athletes'
        />
      </form>
      {showResults && (
        <div
          id='search-module'
          className='bg-background-quaternary rounded shadow global-search-outer-container absolute z-10 overflow-hidden py-4 md:py-6 left-[-43px] md:left-0 right-[-43px] md:right-0 mt-2 md:mt-0 max-h-[80vh] overflow-y-auto'
        >
          <div className='flex justify-between w-full border-b border-border-secondary pb-2'>
            <h4 className='m-0 text-text-grey-600 mini-text px-4 md:px-6'>
              SEARCH RESULTS
            </h4>
          </div>
          <div id='global-search-container'>{renderResults()}</div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
